
import Vue from "vue";
import { mapState, mapActions, mapMutations } from "vuex";
import {
  filterDays,
  updateTable,
  updateFirstBookedStatus,
  updateCancelledStatuses
} from "@/api";
import { convertValues, formatDates } from "@/helpers";
import { constants } from "@/utils/constants";
import { startCase } from "lodash";
import { headersInterface } from "@/interfaces/Tables.interfaces";
import PhoneInfoModal from "../components/PhoneInfoModal";
import ShiftsInDialog from "./components/ShiftsInDialog";

export default Vue.extend({
  name: "FirstShiftBooked",
  components: {
    PhoneInfoModal,
    ShiftsInDialog
  },
  data: () => {
    return {
      tableSearch: "",
      showModal: false,
      modalHeader: "",
      modalReason: "",
      btnSubmit: "Submit",
      btnCancel: "Cancel",
      modalData: {
        id: 0,
        firstName: "",
        email: ""
      },
      fBookedStatus: "" as string | number,
      tempData: undefined as any,
      showSameCancelled: false
    };
  },
  computed: {
    ...mapState({
      tablename: "tablename",
      headers: "tableHeaders",
      content: "tableContent",
      countDays: "countDays"
    }),
    tableHeaders(): Array<headersInterface> {
      const tableColumns = constants.enabledColumns.firstShiftBooked;
      const data = tableColumns.map(i => {
        return { align: "left", text: startCase(i), value: i };
      });
      return [
        ...data,
        { align: "left", text: "User shifts", value: "userShifts" },
        { align: "left", text: "Actions", value: "actions" }
      ];
    },
    formattedContent(): Array<any> {
      return convertValues(this.content, { ffsStatus: true });
    },
    filteredContent(): any[] {
      const items = this.formattedContent || [];
      const search = ((this as any).tableSearch || "").trim().toLowerCase();
      
      if (!search) {
        return items;
      }

      // Only search these relevant columns
      const searchableColumns = [
        "JobId",
        "HygienistId",
        "firstName",
        "lastName",
        "email",
        "hygienistEmail",
        "dentistEmail",
        "city",
        "province",
        "phone",
        "CDHONumber",
        "locationName",
        "primaryContact"
      ];

      return items.filter((item: any) => {
        return searchableColumns.some(column => {
          const value = item[column];
          if (value === null || value === undefined) {
            return false;
          }
          return value.toString().toLowerCase().includes(search);
        });
      });
    }
  },
  methods: {
    ...mapMutations(["modify"]),
    ...mapActions(["addAdminLogging"]),
    updateCountDays(value) {
      this.$store.commit("modify", {
        property: "countDays",
        value: value
      });
    },
    openModal(item, header) {
      this.modalReason = "";
      this.showModal = true;
      this.modalData = item;
      this.modalHeader = header;
      this.btnSubmit = "Yes, cancel";
      this.btnCancel = "Go Back";
      this.fBookedStatus = 3;
    },
    async filterDays() {
      let data = await filterDays(this.countDays, this.tablename.toLowerCase());
      this.$store.commit("modify", {
        property: "tableContent",
        value: data.length ? formatDates(data) : []
      });
    },
    async removeRecord(id, prevValue, value) {
      await updateTable({
        tablename: "Job",
        columnname: "firstBooked",
        id,
        value,
        prevValue
      });
      await this.filterDays();
    },
    async ignoreRecord(item) {
      await updateTable({
        tablename: "Job",
        columnname: "firstBooked",
        id: item.JobId,
        value: 6,
        prevValue: item.firstBooked
      });
      await this.filterDays();
    },
    async firstBookedStatus(item, status) {
      await updateFirstBookedStatus({
        data: item,
        status: status
      });
      await this.filterDays();
    },
    async cancelled() {
      if (this.tempData !== undefined) {
        await updateCancelledStatuses({
          data: this.tempData
        });
        this.tempData = undefined;
        this.fBookedStatus = "";
        this.showModal = false;
        this.modalHeader = "";
        this.modalReason = "";
        await this.filterDays();
      } else {
        this.showModal = false;
        let data = await updateFirstBookedStatus({
          data: this.modalData,
          status: this.fBookedStatus,
          reason: this.modalReason
        });
        if (data.length > 0) {
          this.tempData = data;
          this.showSameCancelled = true;
          this.modalHeader = "Future shifts";
          this.modalReason = this.futureShifts(data);
          this.btnSubmit = "Yes, cancel them";
          this.btnCancel = "Don't cancel";
          this.showModal = true;
        } else {
          await this.filterDays();
        }
      }
    },
    async addReviewHistory(item, text) {
      await this.addAdminLogging({
        adminId: parseInt(localStorage.getItem("adminId")!),
        buttonUsed: text,
        hygienistId: item.HygienistId,
        firstName: item.firstName,
        lastName: item.lastName,
        locationId: item.locationId,
        practiceName: item.practiceName,
        email: item.dentistEmail
      });
    },
    futureShifts(data: any): string {
      let html = data.map(item => {
        return `<tr><td>${item.id}</td><td>${item.startDate}</td><td>${item.locationId}</td><td>${item.practiceName}</td></tr>`;
      });

      return `<b>This person has ${data.length} future shifts. Cancel all future shifts?</b><table><tr><th>Job ID</th><th>Start Date</th><th>Location ID</th><th>Practice Name</th></tr>${html}</table>`;
    }
  }
});
